import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  MenuItem,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useMemo } from "react";
import {
  type Control,
  type Resolver,
  FormProvider,
  useForm,
} from "react-hook-form";

import {
  CUSTOM_PRICING_BUNDLE_ID,
  REASON_CODES,
  makeBulkChangeTenantsPricingAssignmentSchema,
  makeChangeTenantPricingAssignmentSchema,
  makeCreateSubaccountPricingAssignmentsSchema,
} from "@stw/yup-validators";

import {
  ChangeTenantPricingAssignmentMutation,
  ChangeTenantPricingAssignmentMutationVariables,
  CreateSubaccountPricingAssignmentsMutation,
  CreateSubaccountPricingAssignmentsMutationVariables,
  GetActivePricingBundlesQuery,
  GetActiveSkusQuery,
  ListTenantsWithSubaccountsQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  SubaccountDropdown,
  SubaccountDropdownItem,
  TenantDropdown,
} from "~frontend/components/forms/ClientDropdowns";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfCurrencyField,
  RhfDatePicker,
  RhfErrorBanner,
  RhfSelect,
  RhfSwitchField,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CHANGE_TENANT_PRICING_ASSIGNMENT,
  CREATE_SUBACCOUNT_PRICING_ASSIGNMENTS,
  GET_ACTIVE_PRICING_BUNDLES,
  GET_TENANT_PRICING,
  LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
} from "~frontend/graphql/pricingBundle";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { compareSkuNames, reasonCodeLabel, toTimestamp } from "./helpers";

type Sku = GetActiveSkusQuery["getActiveSkus"][0];

type Bundle = GetActivePricingBundlesQuery["getActivePricingBundles"][0];

// Form values use DateTime | null for date pickers; the shared Zod schema
// transforms them to Date during validation. Custom-pricing inputs are stored
// as an array of `{ skuId, priceInMillicents }` — the same shape the mutation
// expects — so RHF addresses each input at `<prefix>.<index>.priceInMillicents`.
// `priceInMillicents` is `number | ""` because RhfCurrencyField holds an empty
// string while the input is being cleared/edited.
type CustomPriceFormEntry = { skuId: string; priceInMillicents: number | "" };

type FormValues = {
  tenantIds: string[];
  subaccountIds: string[];
  initialBundleId: string;
  initialReasonCode: string;
  initialNotes: string;
  effectiveDate: DateTime | null;
  expirationDate: DateTime | null;
  followUpBundleId: string;
  followUpReasonCode: string;
  followUpNotes: string;
  initialCustomPrices: CustomPriceFormEntry[];
  followUpCustomPrices: CustomPriceFormEntry[];
  customIsWeText: boolean;
};

export type TenantDropdownTenants =
  ListTenantsWithSubaccountsQuery["listTenants"];

export enum ChangePricingDialogMode {
  Tenant = "tenant",
  Subaccount = "subaccount",
  BulkTenants = "bulkTenants",
}

type TenantModeProps = {
  open: boolean;
  onClose: () => void;
  bundles: Bundle[];
  skus: Sku[];
  mode?: ChangePricingDialogMode.Tenant;
  tenantId: string;
  tenantName: string;
  currentBundleId: string;
};

type SubaccountModeProps = {
  open: boolean;
  onClose: () => void;
  bundles: Bundle[];
  skus: Sku[];
  mode: ChangePricingDialogMode.Subaccount;
  tenantId: string;
  subaccounts: SubaccountDropdownItem[];
  // When set, the subaccount dropdown is hidden and this subaccount is the
  // sole target. Used by the staff pricing page's per-row "Change Pricing"
  // action where the subaccount is already chosen by context.
  lockedSubaccountId?: string;
};

type BulkTenantsModeProps = {
  open: boolean;
  onClose: () => void;
  bundles: Bundle[];
  skus: Sku[];
  mode: ChangePricingDialogMode.BulkTenants;
  tenants: TenantDropdownTenants;
};

export type ChangePricingDialogProps =
  | TenantModeProps
  | SubaccountModeProps
  | BulkTenantsModeProps;

const DEFAULT_FORM_VALUES: FormValues = {
  tenantIds: [],
  subaccountIds: [],
  initialBundleId: "",
  initialReasonCode: "",
  initialNotes: "",
  effectiveDate: null,
  expirationDate: null,
  followUpBundleId: "",
  followUpReasonCode: "",
  followUpNotes: "",
  initialCustomPrices: [],
  followUpCustomPrices: [],
  customIsWeText: false,
};

// The form stores prices in millicents directly (no rounding to cents) by
// configuring RhfCurrencyField with `decimalPlaces={5}` — 1 dollar = 100,000
// millicents. This preserves sub-cent rack prices like 1500 millicents
// (= $0.015) round-trip without precision loss.
const MILLICENT_DECIMAL_PLACES = 5;

function defaultPriceList(skus: Sku[]): CustomPriceFormEntry[] {
  return skus.map(s => ({
    skuId: s.id,
    priceInMillicents: s.rackPriceInMillicents,
  }));
}

function customPricesToArray(
  entries: CustomPriceFormEntry[]
): Array<{ skuId: string; priceInMillicents: number }> {
  return entries
    .filter(
      (e): e is { skuId: string; priceInMillicents: number } =>
        typeof e.priceInMillicents === "number"
    )
    .map(({ skuId, priceInMillicents }) => ({ skuId, priceInMillicents }));
}

function CustomPriceInputs({
  control,
  namePrefix,
  skus,
  title,
}: {
  control: Control<FormValues>;
  namePrefix: "initialCustomPrices" | "followUpCustomPrices";
  skus: Sku[];
  title: string;
}): ReactElement {
  return (
    <FlexBox direction="column">
      <FlexBox
        alignItems="center"
        sx={{ px: 2, height: "2.5em", backgroundColor: palette.lightGray }}
      >
        <Typography variant="body2Bold">{title}</Typography>
      </FlexBox>
      <TwoColumnDisplay
        allRowSx={{
          backgroundColor: palette.lightestGray,
          px: 2,
        }}
        labelWidth="200px"
        labelVariant="body2Bold"
        contentVariant="body2"
        rows={[...skus]
          .sort((a, b) => compareSkuNames(a.name, b.name))
          .map((sku, index) => ({
            key: sku.id,
            label: sku.name,
            content: (
              <RhfCurrencyField
                control={control}
                name={`${namePrefix}.${index}.priceInMillicents` as const}
                label=""
                isSlim
                decimalPlaces={MILLICENT_DECIMAL_PLACES}
              />
            ),
          }))}
        showTopBorder={false}
        showBottomBorder={false}
      />
    </FlexBox>
  );
}

function BundlePricePreview({ bundle }: { bundle: Bundle }): ReactElement {
  return (
    <FlexBox direction="column">
      <FlexBox
        alignItems="center"
        sx={{ px: 2, height: "2.5em", backgroundColor: palette.lightGray }}
      >
        <Typography variant="body2Bold">{bundle.name} Pricing</Typography>
      </FlexBox>
      <TwoColumnDisplay
        allRowSx={{
          backgroundColor: palette.lightestGray,
          px: 2,
        }}
        labelWidth="200px"
        labelVariant="body2Bold"
        contentVariant="body2"
        rows={[...bundle.prices]
          .sort((a, b) => compareSkuNames(a.sku.name, b.sku.name))
          .map(price => ({
            key: price.id,
            label: price.sku.name,
            content: `$${formatMillicentsAsDollars(price.priceInMillicents)}`,
          }))}
        showTopBorder={false}
        showBottomBorder={false}
      />
    </FlexBox>
  );
}

export function ChangePricingDialog({
  open,
  onClose,
  bundles,
  skus,
  ...props
}: ChangePricingDialogProps): ReactElement {
  const isSubaccountMode = props.mode === ChangePricingDialogMode.Subaccount;
  const isBulkTenantsMode = props.mode === ChangePricingDialogMode.BulkTenants;
  const isTenantMode = !isSubaccountMode && !isBulkTenantsMode;
  const subaccountsProp = isSubaccountMode ? props.subaccounts : undefined;
  const subaccounts = useMemo<SubaccountDropdownItem[]>(
    () => subaccountsProp ?? [],
    [subaccountsProp]
  );
  const tenantsProp = isBulkTenantsMode ? props.tenants : undefined;
  const tenants = useMemo<TenantDropdownTenants>(
    () => tenantsProp ?? [],
    [tenantsProp]
  );
  const lockedSubaccountId =
    isSubaccountMode && "lockedSubaccountId" in props
      ? props.lockedSubaccountId
      : undefined;
  const subaccountTenantId = isSubaccountMode ? props.tenantId : undefined;
  const currentBundleId = isTenantMode ? props.currentBundleId : undefined;
  const targetTenantId = isTenantMode ? props.tenantId : undefined;
  const targetTenantName = isTenantMode ? props.tenantName : undefined;

  // The per-SKU custom-price cap is each SKU's price in the non-WeText
  // Standard bundle plus the offset baked into the validator. If the Standard
  // bundle (or any individual SKU's row) hasn't loaded yet, that SKU has no
  // cap — only the floor/integer checks remain.
  const standardPricesBySkuId = useMemo<Record<string, number>>(() => {
    const standard = bundles.find(b => b.name === "Standard" && !b.isWeText);
    if (!standard) {
      return {};
    }
    return Object.fromEntries(
      standard.prices.map(p => [p.sku.id, p.priceInMillicents])
    );
  }, [bundles]);

  const resolver = useMemo<Resolver<FormValues>>(() => {
    if (isSubaccountMode) {
      return zodResolver(
        makeCreateSubaccountPricingAssignmentsSchema({ standardPricesBySkuId })
      ) as unknown as Resolver<FormValues>;
    }
    if (isBulkTenantsMode) {
      return zodResolver(
        makeBulkChangeTenantsPricingAssignmentSchema({ standardPricesBySkuId })
      ) as unknown as Resolver<FormValues>;
    }
    return zodResolver(
      makeChangeTenantPricingAssignmentSchema({ standardPricesBySkuId })
    ) as unknown as Resolver<FormValues>;
  }, [isSubaccountMode, isBulkTenantsMode, standardPricesBySkuId]);

  const initialFormValues = useMemo<FormValues>(
    () => ({
      ...DEFAULT_FORM_VALUES,
      subaccountIds: lockedSubaccountId ? [lockedSubaccountId] : [],
      initialCustomPrices: defaultPriceList(skus),
      followUpCustomPrices: defaultPriceList(skus),
    }),
    [skus, lockedSubaccountId]
  );

  const methods = useForm<FormValues>({
    resolver,
    defaultValues: initialFormValues,
    mode: "onSubmit",
  });

  const { control, handleSubmit, watch, setError, formState, reset, setValue } =
    methods;

  const [changePricing] = useMutation<
    ChangeTenantPricingAssignmentMutation,
    ChangeTenantPricingAssignmentMutationVariables
  >(CHANGE_TENANT_PRICING_ASSIGNMENT, {
    refetchQueries: [
      GET_TENANT_PRICING,
      GET_ACTIVE_PRICING_BUNDLES,
      LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
    ],
    awaitRefetchQueries: true,
  });

  const [createSubaccountPricing] = useMutation<
    CreateSubaccountPricingAssignmentsMutation,
    CreateSubaccountPricingAssignmentsMutationVariables
  >(CREATE_SUBACCOUNT_PRICING_ASSIGNMENTS, {
    refetchQueries: [
      GET_TENANT_PRICING,
      GET_ACTIVE_PRICING_BUNDLES,
      LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
    ],
    awaitRefetchQueries: true,
  });

  const formatErrorMessage = useErrorMessageFormatter();

  const selectedSubaccountIds = watch("subaccountIds");
  const selectedTenantIds = watch("tenantIds");
  const initialBundleId = watch("initialBundleId");
  const followUpBundleId = watch("followUpBundleId");
  const expirationDate = watch("expirationDate");
  const initialReasonCode = watch("initialReasonCode");
  const followUpReasonCode = watch("followUpReasonCode");
  const initialIsCustom = initialBundleId === CUSTOM_PRICING_BUNDLE_ID;
  const followUpIsCustom = followUpBundleId === CUSTOM_PRICING_BUNDLE_ID;

  // In subaccount mode, WeText and non-WeText subaccounts cannot be mixed in
  // one assignment — pick the lock from the first selected subaccount and
  // disable the opposite type. Reset when selection clears.
  const weTextLock: boolean | null = useMemo(() => {
    if (!isSubaccountMode || selectedSubaccountIds.length === 0) {
      return null;
    }
    const first = subaccounts.find(s => s.id === selectedSubaccountIds[0]);
    return first?.isWeText ?? null;
  }, [isSubaccountMode, selectedSubaccountIds, subaccounts]);

  const disabledSubaccountIds = useMemo(() => {
    if (!isSubaccountMode || weTextLock === null) {
      return undefined;
    }
    return subaccounts
      .filter(s => (s.isWeText ?? false) !== weTextLock)
      .map(s => s.id);
  }, [isSubaccountMode, subaccounts, weTextLock]);

  const visibleBundles = useMemo(() => {
    if (!isSubaccountMode || weTextLock === null) {
      return bundles;
    }
    return bundles.filter(b => b.isWeText === weTextLock);
  }, [isSubaccountMode, bundles, weTextLock]);

  // If the selected bundle is no longer visible after a WeText lock change,
  // clear it so the form doesn't submit with a hidden-but-stale value. The
  // sentinel ("Custom Pricing") is always selectable and never filtered out.
  useEffect(() => {
    if (
      initialBundleId &&
      initialBundleId !== CUSTOM_PRICING_BUNDLE_ID &&
      !visibleBundles.some(b => b.id === initialBundleId)
    ) {
      setValue("initialBundleId", "");
      setValue("initialCustomPrices", defaultPriceList(skus));
    }
    if (
      followUpBundleId &&
      followUpBundleId !== CUSTOM_PRICING_BUNDLE_ID &&
      !visibleBundles.some(b => b.id === followUpBundleId)
    ) {
      setValue("followUpBundleId", "");
      setValue("followUpCustomPrices", defaultPriceList(skus));
    }
  }, [visibleBundles, initialBundleId, followUpBundleId, setValue, skus]);

  const selectedInitialBundle = bundles.find(b => b.id === initialBundleId);
  const selectedFollowUpBundle = bundles.find(b => b.id === followUpBundleId);
  const hasExpiration = expirationDate != null;

  function clearAfterFields() {
    setValue("followUpBundleId", "");
    setValue("followUpReasonCode", "");
    setValue("followUpNotes", "");
    setValue("followUpCustomPrices", defaultPriceList(skus));
  }

  function handleClose() {
    reset(initialFormValues);
    onClose();
  }

  async function onSubmit(values: FormValues) {
    // Zod transforms DateTime → Date at runtime; cast here to reflect that.
    const effectiveDate = values.effectiveDate as unknown as Date;
    const expirationDate = values.expirationDate as unknown as Date | null;

    const initialUsingCustomPricing =
      values.initialBundleId === CUSTOM_PRICING_BUNDLE_ID;
    const followUpUsingCustomPricing =
      values.followUpBundleId === CUSTOM_PRICING_BUNDLE_ID;
    const sharedInput = {
      initialBundleId: values.initialBundleId,
      initialReasonCode: values.initialReasonCode,
      initialNotes: values.initialNotes || null,
      effectiveDate: toTimestamp(effectiveDate),
      expirationDate: expirationDate ? toTimestamp(expirationDate) : null,
      followUpBundleId: values.followUpBundleId || null,
      followUpReasonCode: values.followUpReasonCode || null,
      followUpNotes: values.followUpNotes || null,
      initialCustomPrices: initialUsingCustomPricing
        ? customPricesToArray(values.initialCustomPrices)
        : null,
      followUpCustomPrices: followUpUsingCustomPricing
        ? customPricesToArray(values.followUpCustomPrices)
        : null,
    };

    if (isSubaccountMode) {
      const { errors } = await createSubaccountPricing({
        variables: {
          input: {
            ...sharedInput,
            tenantId: subaccountTenantId!,
            subaccountIds: values.subaccountIds,
          },
        },
      });
      if (errors) {
        setError("root", { message: formatErrorMessage(errors) });
      } else {
        handleClose();
      }
      return;
    }

    const tenantIdsToSubmit = isBulkTenantsMode
      ? values.tenantIds
      : targetTenantId
        ? [targetTenantId]
        : [];

    if (tenantIdsToSubmit.length === 0) {
      setError("tenantIds", { message: "Required" });
      return;
    }

    const { data, errors } = await changePricing({
      variables: {
        input: {
          ...sharedInput,
          tenantIds: tenantIdsToSubmit,
          customIsWeText: initialUsingCustomPricing
            ? values.customIsWeText
            : null,
        },
      },
    });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
      return;
    }

    const result = data?.changeTenantPricingAssignment;
    if (result && result.failures.length > 0) {
      // Best-effort: some tenants succeeded, some failed. Surface a combined
      // message inside the dialog without closing so the user can see which
      // tenants failed and why.
      const tenantNameById = new Map(tenants.map(t => [t.id, t.name]));
      const message = result.failures
        .map(f => {
          const label =
            tenantNameById.get(f.tenantId) ?? targetTenantName ?? "Tenant";
          return `${label}: ${f.errorMessage}`;
        })
        .join("\n");
      setError("root", { message });
      return;
    }

    handleClose();
  }

  const lockedSubaccount = lockedSubaccountId
    ? subaccounts.find(s => s.id === lockedSubaccountId)
    : undefined;

  const title: ReactElement | string = isSubaccountMode ? (
    lockedSubaccount ? (
      <>
        Change Pricing for{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {lockedSubaccount.name}
        </Box>
      </>
    ) : (
      "Add Subaccount Specific Pricing"
    )
  ) : isBulkTenantsMode ? (
    "Bulk Change Pricing"
  ) : (
    <>
      Change Pricing for{" "}
      <Box component="span" sx={{ fontWeight: "bold" }}>
        {targetTenantName}
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h1" sx={{ pt: 3, pb: 1 }}>
        {title}
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 0 }}
          >
            <FlexBox flexDirection="column" gap={0}>
              <RhfErrorBanner control={control} />

              {isSubaccountMode && !lockedSubaccountId && (
                <Box sx={{ pb: 1 }}>
                  <SubaccountDropdown
                    multiple
                    subaccounts={subaccounts}
                    disabledSubaccountIds={disabledSubaccountIds}
                    value={selectedSubaccountIds}
                    onChange={(newIds: string[]) => {
                      setValue("subaccountIds", newIds, {
                        shouldValidate: formState.isSubmitted,
                      });
                    }}
                    label="Subaccounts"
                    placeholder="Search subaccounts..."
                  />
                  {formState.errors.subaccountIds?.message ? (
                    <FormHelperText error>
                      {formState.errors.subaccountIds.message}
                    </FormHelperText>
                  ) : null}
                </Box>
              )}

              {isBulkTenantsMode && (
                <Box sx={{ pb: 1 }}>
                  <TenantDropdown
                    multiple
                    tenants={tenants}
                    value={selectedTenantIds}
                    onChange={(newIds: string[]) => {
                      setValue("tenantIds", newIds, {
                        shouldValidate: formState.isSubmitted,
                      });
                    }}
                    label="Tenants"
                    placeholder="Search tenants..."
                  />
                  {formState.errors.tenantIds?.message ? (
                    <FormHelperText error>
                      {formState.errors.tenantIds.message}
                    </FormHelperText>
                  ) : null}
                </Box>
              )}

              <RhfSelect
                control={control}
                name="initialBundleId"
                label="Pricing Option"
                fullWidth
                onChange={(value: unknown) => {
                  if (
                    value === followUpBundleId &&
                    value !== CUSTOM_PRICING_BUNDLE_ID
                  ) {
                    clearAfterFields();
                  }
                }}
              >
                {visibleBundles
                  .filter(bundle => bundle.id !== currentBundleId)
                  .map(bundle => (
                    <MenuItem key={bundle.id} value={bundle.id}>
                      {bundle.name}
                    </MenuItem>
                  ))}
                <MenuItem value={CUSTOM_PRICING_BUNDLE_ID}>
                  Custom Pricing
                </MenuItem>
              </RhfSelect>

              {selectedInitialBundle && (
                <Box sx={{ pb: 2 }}>
                  <BundlePricePreview bundle={selectedInitialBundle} />
                </Box>
              )}

              {initialIsCustom && (
                <Box sx={{ pb: 2 }}>
                  {!isSubaccountMode && (
                    <Box sx={{ pb: 1 }}>
                      <RhfSwitchField
                        control={control}
                        name="customIsWeText"
                        label="WeText pricing"
                      />
                    </Box>
                  )}
                  <CustomPriceInputs
                    control={control}
                    namePrefix="initialCustomPrices"
                    skus={skus}
                    title="Custom Pricing"
                  />
                  {typeof formState.errors.initialCustomPrices?.message ===
                  "string" ? (
                    <FormHelperText error>
                      {formState.errors.initialCustomPrices.message}
                    </FormHelperText>
                  ) : null}
                </Box>
              )}

              <RhfSelect
                control={control}
                name="initialReasonCode"
                label="Change Reason"
                fullWidth
              >
                {REASON_CODES.map(code => (
                  <MenuItem key={code} value={code}>
                    {reasonCodeLabel(code)}
                  </MenuItem>
                ))}
              </RhfSelect>

              <RhfTextField
                control={control}
                name="initialNotes"
                label="Notes"
                fullWidth
                multiline
                rows={1}
                optional={initialReasonCode !== "OTHER"}
              />

              <FlexBox alignItems="flex-start" gap={2}>
                <RhfDatePicker
                  control={control}
                  name="effectiveDate"
                  label="Effective Date"
                  fullWidth
                  timezone="utc"
                  sx={{ maxHeight: "40px" }}
                />
                <RhfDatePicker
                  control={control}
                  name="expirationDate"
                  label="Expiration Date"
                  fullWidth
                  optional
                  onChange={(value: unknown) => {
                    if (value == null) {
                      clearAfterFields();
                    }
                  }}
                  timezone="utc"
                  sx={{ maxHeight: "40px" }}
                />
              </FlexBox>
            </FlexBox>

            {hasExpiration && (
              <FlexBox flexDirection="column" gap={0}>
                <RhfSelect
                  control={control}
                  name="followUpBundleId"
                  label={
                    isSubaccountMode
                      ? "Pricing Option to Apply After Expiration Date (optional)"
                      : "Pricing Option to Apply After Expiration Date"
                  }
                  fullWidth
                >
                  {isSubaccountMode && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {visibleBundles
                    .filter(bundle => bundle.id !== initialBundleId)
                    .map(bundle => (
                      <MenuItem key={bundle.id} value={bundle.id}>
                        {bundle.name}
                      </MenuItem>
                    ))}
                  <MenuItem value={CUSTOM_PRICING_BUNDLE_ID}>
                    Custom Pricing
                  </MenuItem>
                </RhfSelect>

                {selectedFollowUpBundle &&
                  selectedFollowUpBundle.id !== initialBundleId && (
                    <Box sx={{ pb: 2 }}>
                      <BundlePricePreview bundle={selectedFollowUpBundle} />
                    </Box>
                  )}

                {followUpIsCustom && (
                  <Box sx={{ pb: 2 }}>
                    <CustomPriceInputs
                      control={control}
                      namePrefix="followUpCustomPrices"
                      skus={skus}
                      title="Custom Pricing (after expiration)"
                    />
                    {typeof formState.errors.followUpCustomPrices?.message ===
                    "string" ? (
                      <FormHelperText error>
                        {formState.errors.followUpCustomPrices.message}
                      </FormHelperText>
                    ) : null}
                  </Box>
                )}

                {(!isSubaccountMode || followUpBundleId) && (
                  <>
                    <RhfSelect
                      control={control}
                      name="followUpReasonCode"
                      label="Change Reason"
                      fullWidth
                    >
                      {REASON_CODES.map(code => (
                        <MenuItem key={code} value={code}>
                          {reasonCodeLabel(code)}
                        </MenuItem>
                      ))}
                    </RhfSelect>

                    <RhfTextField
                      control={control}
                      name="followUpNotes"
                      label="Notes"
                      fullWidth
                      multiline
                      rows={1}
                      optional={followUpReasonCode !== "OTHER"}
                    />
                  </>
                )}
              </FlexBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={formState.isSubmitting}
              onClick={handleClose}
              color="secondary"
            >
              Cancel
            </Button>
            <SubmitButton
              isSubmitting={formState.isSubmitting}
              submitText="Save"
              loadingText="Saving..."
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
